<template>
  <div class="dashboard">

    <!-- Loading -->
    <div v-if="loading" class="dashboard-loading">
      <i class="ri-loader-4-line ri-spin"></i>
      <p>Cargando dashboard...</p>
    </div>

    <template v-else-if="dashboard">

      <!-- ============================================================= -->
      <!-- CABECERA: saludo + filtro empresa                              -->
      <!-- ============================================================= -->
      <div class="dashboard-header">
        <div class="greeting">
          <h1>{{ greeting }}</h1>
          <span class="date-text">{{ todayFormatted }}</span>
        </div>
        <div class="company-filter">
          <FormSelect
            :items="companyOptions"
            v-model="selectedCompanyId"
            defaultOption="Todas las empresas"
            @input="onCompanyChanged"
          />
        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 1: Requiere tu atención                                 -->
      <!-- ============================================================= -->
      <div class="dashboard-section" v-if="hasAlerts">
        <div class="section-title">
          <i class="ri-alert-line"></i>
          Requiere tu atención
        </div>

        <div
          v-for="alert in alertItems"
          :key="alert.key"
          :class="['alert-card', 'alert-' + alert.color]"
        >
          <div class="alert-body">
            <div class="alert-number">{{ alert.count }}</div>
            <div class="alert-text">{{ alert.text }}</div>
          </div>
          <a href="#" class="alert-link" @click.prevent="$router.push(alert.route)">
            Ver <i class="ri-arrow-right-s-line"></i>
          </a>
        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 2: Personal activo (4 tarjetas)                         -->
      <!-- ============================================================= -->
      <div class="dashboard-section">
        <div class="section-title">
          <i class="ri-team-line"></i>
          Personal activo
        </div>

        <div class="indicator-cards">
          <!-- Colaboradores -->
          <div class="indicator-card">
            <i class="ri-team-line indicator-icon"></i>
            <div class="indicator-number">{{ indicators.totalEmployees }}</div>
            <div class="indicator-label">Colaboradores</div>
            <div class="indicator-sub" v-if="indicators.newThisMonth > 0">
              <span class="sub-green">+{{ indicators.newThisMonth }}</span> este mes
            </div>
          </div>

          <!-- Contratos vigentes -->
          <div class="indicator-card">
            <i class="ri-file-list-line indicator-icon"></i>
            <div class="indicator-number">{{ indicators.activeContracts }}</div>
            <div class="indicator-label">Contratos vigentes</div>
            <div class="indicator-sub" v-if="indicators.noContractCount > 0">
              <span class="sub-red">{{ indicators.noContractCount }}</span> sin contrato
            </div>
          </div>

          <!-- Ingresos -->
          <div class="indicator-card">
            <i class="ri-user-add-line indicator-icon"></i>
            <div class="indicator-number text-green">{{ indicators.hiresYear }}</div>
            <div class="indicator-label">Ingresos ({{ currentYear }})</div>
            <div class="indicator-sub">
              {{ currentMonthShort }}: <span class="sub-green">{{ indicators.hiresMonth }}</span>
            </div>
          </div>

          <!-- Salidas -->
          <div class="indicator-card">
            <i class="ri-user-unfollow-line indicator-icon"></i>
            <div :class="['indicator-number', indicators.exitsYear > 0 ? 'text-red' : '']">{{ indicators.exitsYear }}</div>
            <div class="indicator-label">Salidas ({{ currentYear }})</div>
            <div class="indicator-sub">
              {{ currentMonthShort }}: <span :class="indicators.exitsMonth > 0 ? 'sub-red' : ''">{{ indicators.exitsMonth }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 3: Hoy (ausencias + cumpleaños)                         -->
      <!-- ============================================================= -->
      <div class="dashboard-section">
        <div class="today-cards">

          <!-- Ausencias de hoy -->
          <div class="today-card">
            <div class="today-card-header">
              <span class="today-card-title">Ausencias de hoy</span>
              <span class="badge badge-gray today-card-badge" v-if="absencesToday.length > 0">
                {{ absencesToday.length }} persona(s)
              </span>
            </div>

            <div v-if="absencesToday.length === 0" class="today-empty">
              <i class="ri-checkbox-circle-line" style="color: #28a745"></i>
              <p>Todos los colaboradores están presentes hoy</p>
            </div>

            <ul v-else class="today-list">
              <li v-for="a in absencesVisible" :key="a.employeeId">
                <div class="avatar-circle">{{ a.initials }}</div>
                <div class="person-info">
                  <div class="person-name">{{ a.name }}</div>
                  <div class="person-detail">
                    <span
                      :class="['badge', a.type === 'VACATION' ? 'badge-blue' : 'badge-warning']"
                      style="font-size: 11px"
                    >
                      {{ a.type === 'VACATION' ? 'Vacaciones' : 'Desc. médico' }}
                    </span>
                  </div>
                </div>
              </li>
            </ul>

            <a
              v-if="absencesToday.length > 5"
              href="#"
              class="today-more"
              @click.prevent="showAllAbsences = !showAllAbsences"
            >
              {{ showAllAbsences ? 'Ver menos' : 'Ver todas (+' + (absencesToday.length - 5) + ' más)' }}
            </a>
          </div>

          <!-- Cumpleaños del mes -->
          <div class="today-card">
            <div class="today-card-header">
              <span class="today-card-title">Cumpleaños — {{ currentMonthName }}</span>
              <span class="badge badge-gray today-card-badge" v-if="birthdays.length > 0">
                {{ birthdays.length }}
              </span>
            </div>

            <div v-if="birthdays.length === 0" class="today-empty">
              <i class="ri-cake-line"></i>
              <p>No hay cumpleaños este mes</p>
            </div>

            <ul v-else class="today-list">
              <li
                v-for="b in birthdaysVisible"
                :key="b.employeeId"
                :class="{ 'birthday-today': b.today }"
              >
                <div class="avatar-circle">{{ b.initials }}</div>
                <div class="person-info">
                  <div class="person-name">
                    {{ b.name }}
                    <i v-if="b.today" class="ri-cake-2-line ml-1" style="color: #ffc107"></i>
                  </div>
                  <div class="person-detail">{{ b.birthDate }}</div>
                </div>
              </li>
            </ul>

            <a
              v-if="birthdays.length > 5"
              href="#"
              class="today-more"
              @click.prevent="showAllBirthdays = !showAllBirthdays"
            >
              {{ showAllBirthdays ? 'Ver menos' : 'Ver todos (+' + (birthdays.length - 5) + ' más)' }}
            </a>
          </div>

        </div>
      </div>

      <!-- ============================================================= -->
      <!-- BLOQUE 4: Estado del mes (asistencia + planilla)               -->
      <!-- ============================================================= -->
      <div class="dashboard-section">
        <div class="section-title">
          <i class="ri-calendar-check-line"></i>
          Estado del mes
        </div>

        <div class="today-cards">

          <!-- Asistencia del mes -->
          <div class="today-card">
            <div class="today-card-header">
              <span class="today-card-title">Asistencia — {{ currentMonthName }}</span>
            </div>

            <div v-if="!attendance" class="today-empty">
              <i class="ri-calendar-close-line"></i>
              <p>Sin datos de asistencia para este mes</p>
            </div>

            <div v-else class="att-list">
              <!-- Días hábiles -->
              <div class="att-row">
                <span class="att-label">Días hábiles</span>
                <span class="att-value">{{ attendance.workDaysPassed }} de {{ attendance.totalWorkDays }}</span>
              </div>
              <div class="progress-bar-track">
                <div
                  class="progress-bar-fill bg-blue"
                  :style="{ width: workDaysPercent + '%' }"
                ></div>
              </div>

              <!-- Faltas -->
              <div class="att-row">
                <span class="att-label">Faltas injustificadas</span>
                <span :class="['att-value', attendance.absences > 0 ? 'text-danger' : 'text-muted']">
                  {{ attendance.absences }}
                </span>
              </div>

              <!-- Tardanzas -->
              <div class="att-row">
                <span class="att-label">Tardanzas registradas</span>
                <span :class="['att-value', attendance.tardiness > 0 ? 'text-warning-dark' : 'text-muted']">
                  {{ attendance.tardiness }}
                </span>
              </div>

              <!-- Horas extra -->
              <div class="att-row">
                <span class="att-label">Horas extra acumuladas</span>
                <span class="att-value text-primary">{{ attendance.overtimeHours || 0 }}h</span>
              </div>

              <!-- Asistencia promedio -->
              <div class="att-row">
                <span class="att-label">Asistencia promedio</span>
                <span :class="['badge', attendanceRateBadge]">{{ attendance.attendanceRate || 0 }}%</span>
              </div>
              <div class="progress-bar-track">
                <div
                  :class="['progress-bar-fill', attendanceRateBarColor]"
                  :style="{ width: (attendance.attendanceRate || 0) + '%' }"
                ></div>
              </div>

              <a href="#" class="today-more mt-3" @click.prevent="$router.push({ name: 'attendance-summary' })">
                Ver detalle <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>
          </div>

          <!-- Planilla del mes -->
          <div class="today-card">
            <div class="today-card-header">
              <span class="today-card-title">Planilla — {{ currentMonthName }}</span>
            </div>

            <!-- Sin planilla -->
            <div v-if="!payroll || !payroll.exists" class="today-empty">
              <i class="ri-file-list-line"></i>
              <p>No se ha creado la planilla de {{ currentMonthName }}</p>
              <a href="#" class="today-more" @click.prevent="$router.push({ name: 'payroll-list' })">
                Ir a planillas <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>

            <!-- Con planilla -->
            <div v-else>
              <!-- Stepper -->
              <div class="payroll-stepper">
                <div
                  v-for="(step, idx) in payrollSteps"
                  :key="step.label"
                  :class="['stepper-step', step.state]"
                >
                  <div class="stepper-circle">
                    <i v-if="step.state === 'completed'" class="ri-check-line"></i>
                    <span v-else>{{ idx + 1 }}</span>
                  </div>
                  <div class="stepper-label">{{ step.label }}</div>
                  <div v-if="idx < 3" class="stepper-line" :class="step.state === 'completed' ? 'completed' : ''"></div>
                </div>
              </div>

              <!-- Info -->
              <div class="payroll-info">
                <div class="text-muted small" v-if="payroll.lastStatusDate">
                  Última actualización: {{ payroll.lastStatusDate }}
                  <span v-if="payroll.lastStatusUser"> por {{ payroll.lastStatusUser }}</span>
                </div>
                <div class="mt-1" v-if="payroll.employeeCount > 0">
                  <strong>{{ payroll.employeeCount }}</strong> empleados
                  <span v-if="payroll.totalGross"> · Bruto: S/ {{ formatNumber(payroll.totalGross) }}</span>
                  <span v-if="payroll.totalNet"> · Neto: S/ {{ formatNumber(payroll.totalNet) }}</span>
                </div>
              </div>

              <!-- Movimientos pendientes -->
              <div
                v-if="(payroll.pendingDiscounts || 0) > 0 || (payroll.pendingAdditions || 0) > 0"
                class="payroll-pending-alert"
              >
                <div v-if="payroll.pendingDiscounts > 0">
                  <strong>{{ payroll.pendingDiscounts }}</strong> descuentos sin procesar
                </div>
                <div v-if="payroll.pendingAdditions > 0">
                  <strong>{{ payroll.pendingAdditions }}</strong> adicionales sin procesar
                </div>
                <a href="#" class="today-more mt-1" @click.prevent="$router.push({ name: 'payroll-movement-list' })">
                  Ver movimientos <i class="ri-arrow-right-s-line"></i>
                </a>
              </div>

              <a href="#" class="today-more mt-3" @click.prevent="$router.push({ name: 'payroll-list' })">
                Ver planilla <i class="ri-arrow-right-s-line"></i>
              </a>
            </div>
          </div>

        </div>
      </div>

    </template>
  </div>
</template>

<script>
import { FormSelect, Helpers } from 'wize-admin'
import { DashboardService, CompanyService } from '@/core/services'
import { mapGetters } from 'vuex'

const DAYS = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
const MONTHS = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
]
const MONTHS_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic']

export default {
  name: 'DashboardPage',
  components: { FormSelect },

  data() {
    return {
      loading: true,
      dashboard: null,
      companies: [],
      selectedCompanyId: null,
      showAllAbsences: false,
      showAllBirthdays: false,
    }
  },

  computed: {
    ...mapGetters({ user: 'auth/getUser' }),

    greeting() {
      const hour = new Date().getHours()
      let salute = 'Buenos días'
      if (hour >= 19) salute = 'Buenas noches'
      else if (hour >= 12) salute = 'Buenas tardes'
      const name = this.user ? Helpers.capitalizeName(this.user.name) : ''
      return `${salute}, ${name}`
    },

    todayFormatted() {
      const d = new Date()
      const day = DAYS[d.getDay()]
      const num = d.getDate()
      const month = MONTHS[d.getMonth()]
      const year = d.getFullYear()
      return `${day} ${num} de ${month}, ${year}`
    },

    currentYear() {
      return new Date().getFullYear()
    },

    currentMonthName() {
      return MONTHS[new Date().getMonth()].charAt(0).toUpperCase() + MONTHS[new Date().getMonth()].slice(1)
    },

    currentMonthShort() {
      return MONTHS_SHORT[new Date().getMonth()]
    },

    companyOptions() {
      return this.companies.map(c => ({ key: c.id, label: c.businessName || c.name }))
    },

    alerts() {
      return this.dashboard ? this.dashboard.alerts : {}
    },

    indicators() {
      return this.dashboard ? this.dashboard.indicators : {}
    },

    absencesToday() {
      return this.dashboard ? (this.dashboard.absencesToday || []) : []
    },

    birthdays() {
      return this.dashboard ? (this.dashboard.birthdays || []) : []
    },

    absencesVisible() {
      return this.showAllAbsences ? this.absencesToday : this.absencesToday.slice(0, 5)
    },

    birthdaysVisible() {
      return this.showAllBirthdays ? this.birthdays : this.birthdays.slice(0, 5)
    },

    hasAlerts() {
      const a = this.alerts
      if (!a) return false
      return (a.expiredVacations || 0) > 0
          || (a.pendingVacations || 0) > 0
          || (a.noContract || 0) > 0
          || (a.expiringContracts || 0) > 0
          || (a.expiringVacationPeriods || 0) > 0
          || (a.expiringMedicalLeaves || 0) > 0
          || (a.childrenTurning18 || 0) > 0
          || (a.expiringStudyCertificates || 0) > 0
          || (a.highVacationBalance || 0) > 0
          || (a.probationEnding || 0) > 0
          || (a.upcomingVacations || 0) > 0
          || (a.childrenTurning24 || 0) > 0
    },

    alertItems() {
      const a = this.alerts
      if (!a) return []
      const items = []

      // --- ROJAS (urgentes) ---
      if (a.expiredVacations > 0) {
        items.push({
          key: 'expiredVacations', color: 'red', count: a.expiredVacations,
          text: 'empleado(s) con períodos vacacionales vencidos sin gozar',
          route: { name: 'vacation-history' },
        })
      }
      if (a.pendingVacations > 0) {
        items.push({
          key: 'pendingVacations', color: 'red', count: a.pendingVacations,
          text: 'solicitudes de vacaciones pendientes',
          route: { name: 'vacation-requests-list' },
        })
      }
      if (a.noContract > 0) {
        items.push({
          key: 'noContract', color: 'red', count: a.noContract,
          text: 'empleados sin contrato',
          route: { name: 'employee-list' },
        })
      }
      // --- AMARILLAS (advertencia) ---
      if (a.expiringContracts > 0) {
        items.push({
          key: 'expiringContracts', color: 'yellow', count: a.expiringContracts,
          text: 'contratos por vencer (próximos 30 días)',
          route: { name: 'contracts-list' },
        })
      }
      if (a.expiringVacationPeriods > 0) {
        items.push({
          key: 'expiringVacationPeriods', color: 'yellow', count: a.expiringVacationPeriods,
          text: 'períodos vacacionales por vencer',
          route: { name: 'vacation-history' },
        })
      }
      if (a.expiringMedicalLeaves > 0) {
        items.push({
          key: 'expiringMedicalLeaves', color: 'yellow', count: a.expiringMedicalLeaves,
          text: 'descansos médicos por finalizar',
          route: { name: 'medical-leave-list' },
        })
      }
      if (a.childrenTurning18 > 0) {
        items.push({
          key: 'childrenTurning18', color: 'yellow', count: a.childrenTurning18,
          text: 'hijo(s) próximo(s) a cumplir 18 años sin constancia de estudios',
          route: { name: 'employee-list' },
        })
      }
      if (a.expiringStudyCertificates > 0) {
        items.push({
          key: 'expiringStudyCertificates', color: 'yellow', count: a.expiringStudyCertificates,
          text: 'constancia(s) de estudios por vencer (próximos 30 días)',
          route: { name: 'employee-list' },
        })
      }
      if (a.highVacationBalance > 0) {
        items.push({
          key: 'highVacationBalance', color: 'yellow', count: a.highVacationBalance,
          text: 'empleado(s) con más de 30 días de vacaciones acumuladas',
          route: { name: 'vacation-history' },
        })
      }
      // --- AZULES (info) ---
      if (a.upcomingVacations > 0) {
        items.push({
          key: 'upcomingVacations', color: 'blue', count: a.upcomingVacations,
          text: 'empleado(s) con vacaciones aprobadas en los próximos 15 días',
          route: { name: 'vacation-requests-list' },
        })
      }
      if (a.childrenTurning24 > 0) {
        items.push({
          key: 'childrenTurning24', color: 'blue', count: a.childrenTurning24,
          text: 'hijo(s) próximo(s) a cumplir 24 años (fin de asignación familiar)',
          route: { name: 'employee-list' },
        })
      }
      if (a.probationEnding > 0) {
        items.push({
          key: 'probationEnding', color: 'blue', count: a.probationEnding,
          text: 'empleados en período de prueba (próximos 30 días)',
          route: { name: 'contracts-list' },
        })
      }

      return items
    },

    // Bloque 4
    attendance() {
      return this.dashboard ? this.dashboard.attendance : null
    },

    payroll() {
      return this.dashboard ? this.dashboard.payroll : null
    },

    workDaysPercent() {
      const a = this.attendance
      if (!a || !a.totalWorkDays) return 0
      return Math.round((a.workDaysPassed / a.totalWorkDays) * 100)
    },

    attendanceRateBadge() {
      const rate = this.attendance ? (this.attendance.attendanceRate || 0) : 0
      if (rate >= 95) return 'badge-green'
      if (rate >= 90) return 'badge-yellow'
      return 'badge-danger'
    },

    attendanceRateBarColor() {
      const rate = this.attendance ? (this.attendance.attendanceRate || 0) : 0
      if (rate >= 95) return 'bg-green'
      if (rate >= 90) return 'bg-yellow'
      return 'bg-red'
    },

    payrollSteps() {
      const status = this.payroll ? this.payroll.status : null
      const steps = [
        { label: 'Borrador', key: 'BORRADOR' },
        { label: 'Calculada', key: 'CALCULADA' },
        { label: 'Aprobada', key: 'APROBADA' },
        { label: 'Pagada', key: 'PAGADA' },
      ]

      const statusOrder = ['BORRADOR', 'CALCULADA', 'APROBADA', 'PAGADA']
      const statusIdx = statusOrder.indexOf(status)

      return steps.map((s, i) => {
        if (statusIdx < 0) return { ...s, state: 'pending' }
        if (i < statusIdx) return { ...s, state: 'completed' }
        if (i === statusIdx) return { ...s, state: 'active' }
        return { ...s, state: 'pending' }
      })
    },
  },

  methods: {
    formatNumber(val) {
      if (val == null) return '0.00'
      return Number(val).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    async loadDashboard() {
      try {
        this.loading = true
        const resp = await DashboardService.getDashboard(this.selectedCompanyId)
        this.dashboard = resp.payload
      } catch (e) {
        console.error('Error al cargar dashboard', e)
      } finally {
        this.loading = false
      }
    },

    onCompanyChanged(val) {
      this.selectedCompanyId = val || null
      if (this.selectedCompanyId) {
        sessionStorage.setItem('dashboard_companyId', this.selectedCompanyId)
      } else {
        sessionStorage.removeItem('dashboard_companyId')
      }
      this.loadDashboard()
    },
  },

  async created() {
    const saved = sessionStorage.getItem('dashboard_companyId')
    if (saved) this.selectedCompanyId = Number(saved)

    try {
      const resp = await CompanyService.listAll()
      this.companies = resp.payload || []
    } catch (e) {
      console.error('Error al cargar empresas', e)
    }

    this.loadDashboard()
  },
}
</script>

<style lang="stylus">
@import "../Styles/Dashboard.styl"
</style>
